import { Component, ViewChild, HostListener, OnInit, OnDestroy } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { MatSidenav } from '@angular/material/sidenav';
import { Subject, Subscription } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import * as dayjs from 'dayjs';
import { SearchFilter } from 'ldt-people-api';
import { JobFunctions, JobLevels } from 'src/app/shared/dw-column';
import {
  QuickBuild,
  UIFilter,
  UIFilterGroup,
  UniqueIdGenerator,
} from 'src/app/people/quick-build.service';
import { DateRangePickerComponent } from 'src/app/shared/date-range-picker/date-range-picker.component';
import { SavedListCompanyItem } from 'src/app/shared/saved-lists/saved-lists-models';
import {
  PrevNextFlowComparisonConfig,
  HeadcountComparisonConfig,
  TenureCurrentBreakdownConfig,
  JobStartsAndEndsConfig,
  PrevNextFlowConfig,
  DemographicsConfig,
  DemographicsCurrentConfig,
  LocationMapConfig,
  TopValuesComparisonConfig,
  JobsMovesComparisonConfig,
  HistoricalTenureComparisonConfig,
  TenureCohortComparisonConfig,
} from 'src/app/shared/report-visualizations/interfaces/report-config.interfaces';
import {
  RoleDefinition,
  RoleDefinitionsService,
} from 'src/app/core/services/role-definitions.service';
import { PeopleReportService } from 'src/app/people/report-service.service';
import {
  StreamsSubscribeDrawerService,
  DrawerConfig,
} from 'src/app/shared/streams-subscribe-drawer/streams-subscribe-drawer.service';
import { AuthService } from 'src/app/auth/service/auth.service';
import { Chat, ChatService } from 'ldt-chat-api';
import { PeopleChatService } from 'src/app/core/services/people-chat.service';
import { Artifact } from 'src/app/shared/components/artifact-renderer/artifact-renderer.component';
import { NewChatSubmission } from 'src/app/shared/components/new-chat/new-chat.component';
import { Log } from 'src/app/logger.service';
import { NotificationService } from 'src/app/shared/notification-service/notification.service';
import { LangGraphInstanceService } from 'src/app/core/services/langgraph-instance.service';
import { Observable } from 'rxjs';
import { WorkforceLayoutService } from '../workforce-layout.service';
import { WorkforceAppComponent } from '../app.wf.component';
import { ShareThisViewService } from 'src/app/shared/share-this-view/share-this-view.service';
import { LoginDialogService } from 'src/app/auth/service/login-dialog.service';
import { HeapUtils } from 'src/app/api-client/heap.utils';

export interface UiDirective {
  action: string;
  created_at?: string;
  iteration?: number;
  metadata?: {
    quickbuild?: any;
    quickbuild_id?: string;
    columns?: string[];
    suggested_filename?: string;
    total_people?: number;
    dataframe_id?: string;
    category?: string;
    confidence?: string;
  };
}

// Chart configurations by chart ID
interface ChartConfigurations {
  'talent-flow'?: PrevNextFlowConfig;
  'job-starts-and-ends'?: JobStartsAndEndsConfig;
  'demographics-over-time'?: DemographicsConfig;
  'current-demographics'?: DemographicsCurrentConfig;
  'location-map'?: LocationMapConfig;
  'tenure-current-breakdown'?: TenureCurrentBreakdownConfig;
  'headcount-comparison'?: HeadcountComparisonConfig;
  'top-values-comparison'?: TopValuesComparisonConfig;
  'job-moves-comparison'?: JobsMovesComparisonConfig;
  'historical-tenure-comparison'?: HistoricalTenureComparisonConfig;
  'talent-flow-comparison'?: PrevNextFlowComparisonConfig;
  'tenure-cohort-comparison'?: TenureCohortComparisonConfig;
}

interface TabConfig {
  index: number;
  label: string;
  icon: string;
  enabled: boolean;
}

@Component({
  selector: 'app-chat-with-insights',
  templateUrl: './chat-with-insights.component.html',
  styleUrls: ['./chat-with-insights.component.scss'],
  standalone: false,
})
export class ChatWithInsightsComponent implements OnInit, OnDestroy {
  @ViewChild('sidenavContainer') sidenav!: MatSidenav;
  isSmallScreen$: Observable<boolean>;

  // Sidenav width constants
  private readonly DEFAULT_SIDENAV_WIDTH = 700;
  private readonly MIN_SIDENAV_WIDTH = 500;
  private MIN_CHAT_WIDTH = 500;
  private readonly SIDENAV_RESPONSIVE_BREAKPOINT_PX = 1000;

  // Tab state
  activeTabIndex = 0; // 0=InDepth, 1=HeadToHead, 2=Benchmark, 3=Data, 4=Artifact
  availableTabs: TabConfig[] = [];

  // Local state (NOT shared with standalone insights)
  selectedCompanyIds: Set<string> = new Set();
  selectedCompanies: SavedListCompanyItem[] = [];
  companyLookups: { [name: string]: string } = {};

  dateRangeStrings: [string, string] = [
    dayjs().subtract(2, 'year').format('YYYY-MM'),
    dayjs().format('YYYY-MM'),
  ];

  selectedJobFunctions: JobFunctions[] = [];
  selectedJobLevels: JobLevels[] = [];
  selectedRole: RoleDefinition | null = null;

  // Per-tab chart configs
  chartConfigs: ChartConfigurations = {};

  // Computed QuickBuilds (for each mode)
  inDepthQuickBuild: QuickBuild | null = null;
  headToHeadQBs: QuickBuild[] = [];
  benchmarkQBs: QuickBuild[] = [];

  // Active lists for company-list-editor
  inDepthActiveList: any = { id: null, name: '', companies: [] };
  headToHeadActiveList: any = { id: null, name: '', companies: [] };
  benchmarkActiveList: any = { id: null, name: '', companies: [] };

  // Selected company ID for in-depth view (tracks user selection)
  private inDepthSelectedCompanyId: string | null = null;

  // Track previous company IDs to detect changes
  private previousCompanyIds: Set<string> = new Set();

  protected isAdmin: Observable<boolean>;

  // Selected company IDs for in-depth view
  get inDepthSelectedCompanyIds(): Set<string> {
    if (this.inDepthSelectedCompanyId) {
      return new Set([this.inDepthSelectedCompanyId]);
    }
    if (this.selectedCompanies.length > 0 && this.selectedCompanies[0].id) {
      return new Set([this.selectedCompanies[0].id]);
    }
    return new Set();
  }

  // QuickBuilds from chat messages
  chatQuickBuilds: QuickBuild[] = [];
  selectedQuickBuildIndex: number = 0;

  // QuickBuild for filters component (Data tab)
  quickBuildForFilters: QuickBuild | null = null;

  // Search count from filters
  matchingRecords: number = 0;

  // Sidenav control
  sidenavWidth = this.DEFAULT_SIDENAV_WIDTH; // Will be updated to 50% on init
  sidenavMinWidth = this.MIN_SIDENAV_WIDTH;
  chatMinWidth = this.MIN_CHAT_WIDTH; // Minimum width for chat area
  sidenavMode: 'over' | 'side' = 'side';
  sidenavOpened = false; // Track sidenav state to avoid ExpressionChangedAfterItHasBeenCheckedError
  isResizing = false;
  startX: number = 0;
  startWidth: number = 0;

  reportTarget = 'moneyball';

  // Chat state
  historyDrawerOpen = false;
  activeThreadId: string | undefined;
  primaryThreadHasInsights: boolean = false;

  artifacts: Map<number, Artifact> = new Map(); // messageIndex -> artifact
  selectedArtifact: Artifact | undefined;
  selectedArtifactIndex: number = 0;
  artifactPending: boolean = false; // True when the API indicates an artifact is still generating

  isThreadBusy = false;
  isPalMode = false;
  isSharedThread = false;
  private processedDirectiveKeys = new Set<string>();
  private directiveHandledNavigation = false;
  al_created_at: string | undefined;
  private palModeClickHandler: ((event: Event) => void) | null = null;

  private routerSubscription: Subscription | undefined;
  private breakpointSubscription: Subscription | undefined;
  private threadSubscription: Subscription | undefined;
  private initTimeout?: number;
  private navigationTimeout?: number;
  private resizeTimeout?: number;
  private directiveTimeouts: number[] = [];
  private destroy$ = new Subject<void>();
  private cancelCurrentThreadLoad$ = new Subject<void>(); // Cancel in-flight thread loads

  orgId: string = '';

  constructor(
    private peopleReportService: PeopleReportService,
    private roleDefinitionsService: RoleDefinitionsService,
    private router: Router,
    private route: ActivatedRoute,
    private drawerService: StreamsSubscribeDrawerService,
    private breakpointObserver: BreakpointObserver,
    private authService: AuthService,
    private chatService: ChatService,
    private peopleChatService: PeopleChatService,
    private notifyService: NotificationService,
    private layoutService: WorkforceLayoutService,
    private wfParent: WorkforceAppComponent,
    public langGraphInstanceService: LangGraphInstanceService,
    private shareThisViewService: ShareThisViewService,
    private loginDialogService: LoginDialogService
  ) {
    this.isSmallScreen$ = this.layoutService.isSmallScreen$;
  }

  toggleThinSidenav() {
    this.wfParent.toggleThinSidenav();
  }

  ngOnInit() {
    // Get orgId for chat history
    this.orgId = this.authService.getSelectedOrgIdValue || '';

    // Subscribe to org changes to update orgId
    this.authService.$getSelectedOrgId.pipe(takeUntil(this.destroy$)).subscribe((orgId) => {
      if (orgId && orgId !== this.orgId) {
        this.orgId = orgId;
        // Clear current thread when org changes
        this.activeThreadId = undefined;
        this.primaryThreadHasInsights = false;
        if (this.sidenav) {
          this.sidenav.close();
        }
      }
    });

    // Track threadId from query params and load thread data
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const newThreadId = params['threadId'];
      if (newThreadId && newThreadId !== this.activeThreadId) {
        this.activeThreadId = newThreadId;
        this.isSharedThread = newThreadId.startsWith('shared-');
        this.primaryThreadHasInsights = false;
        this.loadThreadDataAndOpenSidenav(newThreadId);
      } else if (!newThreadId) {
        // No threadId, close sidenav
        this.activeThreadId = undefined;
        this.isSharedThread = false;
        this.primaryThreadHasInsights = false;
        if (this.sidenav) {
          this.sidenav.close();
        }
      }
    });

    // Open the chat history drawer when requested from the sidebar nav
    this.layoutService.openChatHistory$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.historyDrawerOpen = true;
    });
    // Handle a request made while navigating here (we weren't subscribed in time to catch it)
    if (this.layoutService.consumePendingOpenChatHistory()) {
      this.historyDrawerOpen = true;
    }

    // Listen for route changes (when switching chats via history)
    this.routerSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        // Close sidenav when route changes (e.g., selecting different chat from history)
        if (this.sidenav) {
          this.sidenav.close();
        }
      });

    // Responsive sidenav: only update when crossing the breakpoint
    const query = `(max-width: ${this.SIDENAV_RESPONSIVE_BREAKPOINT_PX}px)`;
    this.breakpointSubscription = this.breakpointObserver.observe(query).subscribe((result) => {
      if (result.matches) {
        this.sidenavMode = 'over';
        this.MIN_CHAT_WIDTH = 100;
        this.sidenavMinWidth = 300; // Allow narrower sidenav on mobile
      } else {
        this.sidenavMode = 'side';
        this.MIN_CHAT_WIDTH = 500;
        this.sidenavMinWidth = this.MIN_SIDENAV_WIDTH;
      }
      // Recalculate sidenav width when mode changes
      if (this.sidenav?.opened) {
        this.updateSidenavWidth();
      }
    });

    // Ensure sidenav starts closed
    this.initTimeout = window.setTimeout(() => {
      if (this.sidenav) {
        this.sidenav.close();
      }
    }, 0);

    this.isAdmin = this.authService.$isAdmin;

    // PAL mode: intercept all clicks to prompt signup
    this.al_created_at = history.state?.al_created_at;
    this.authService.$palMode.pipe(takeUntil(this.destroy$)).subscribe((palMode) => {
      this.isPalMode = palMode;
      if (palMode) {
        this.addClickInterception();
      } else {
        this.removeClickInterception();
      }
    });
  }

  ngOnDestroy() {
    // Clear all timeouts
    if (this.initTimeout) {
      window.clearTimeout(this.initTimeout);
    }
    if (this.navigationTimeout) {
      window.clearTimeout(this.navigationTimeout);
    }
    if (this.resizeTimeout) {
      window.clearTimeout(this.resizeTimeout);
    }
    this.directiveTimeouts.forEach((t) => window.clearTimeout(t));
    this.directiveTimeouts = [];

    // Unsubscribe from observables
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
    if (this.breakpointSubscription) {
      this.breakpointSubscription.unsubscribe();
    }
    if (this.threadSubscription) {
      this.threadSubscription.unsubscribe();
    }

    // Remove PAL mode click interception
    this.removeClickInterception();

    // Complete subjects
    this.cancelCurrentThreadLoad$.next();
    this.cancelCurrentThreadLoad$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Load thread data and populate insights sidenav
   * Subscribes to thread updates from service (service handles polling)
   */
  private loadThreadDataAndOpenSidenav(threadId: string) {
    // Allow empty orgId for pre-seeded shared threads — getThread() returns from
    // cache immediately and never makes an API call with the (empty) orgId.
    if (!this.orgId && !this.peopleChatService.getThreadSnapshot(threadId)) {
      console.error('Cannot load thread data - no orgId available');
      return;
    }

    // CRITICAL: Cancel any in-flight thread loading operations
    // This prevents race conditions where a previous thread's subscription
    // completes after we've started loading a new thread
    this.cancelCurrentThreadLoad$.next();

    // Close sidenav immediately when switching threads
    if (this.sidenav) {
      this.sidenav.close();
    }

    // Clear previous thread state
    this.selectedCompanies = [];
    this.selectedCompanyIds = new Set();
    this.previousCompanyIds = new Set();
    this.chatQuickBuilds = [];
    this.availableTabs = [];
    this.artifacts.clear();
    this.selectedArtifact = undefined;
    this.artifactPending = false;
    this.processedDirectiveKeys.clear();

    // Unsubscribe from previous thread updates
    if (this.threadSubscription) {
      this.threadSubscription.unsubscribe();
      this.threadSubscription = undefined;
    }

    // Load initial thread data (will cache it and start polling if busy)
    // Use takeUntil with BOTH destroy$ and cancelCurrentThreadLoad$ to ensure cancellation
    this.peopleChatService
      .getThread(this.orgId, threadId)
      .pipe(
        takeUntil(this.destroy$),
        takeUntil(this.cancelCurrentThreadLoad$) // Cancel if new thread load starts
      )
      .subscribe({
        next: () => {
          // Initial load complete - now subscribe to updates
          // Double-check we're still loading for the same insights thread (in case of rapid switching)
          if (this.activeThreadId !== threadId) {
            return;
          }

          this.threadSubscription = this.peopleChatService
            .threadById$(threadId)
            .pipe(
              takeUntil(this.destroy$),
              takeUntil(this.cancelCurrentThreadLoad$) // Cancel if new thread load starts
            )
            .subscribe({
              next: (thread) => {
                if (thread) {
                  this.handleThreadData(thread);
                }
              },
            });
        },
        error: (err) => {
          console.error('Error loading thread data for insights:', err);
        },
      });
  }

  /**
   * Process thread data and open sidenav with insights
   */
  private handleThreadData(thread: any) {
    // CRITICAL: Only process updates for the thread currently feeding insights
    // This prevents race conditions where updates from a previous thread
    // cause the UI to flip away from the current thread
    if (!thread.id || thread.id !== this.activeThreadId) {
      return;
    }

    // Track whether thread is still processing
    this.isThreadBusy = thread.status === 'busy';

    // Extract company lookups (reverse the mapping: name -> id)
    const companyLookups: { [name: string]: string } = {};
    if (thread.company_lookups) {
      Object.entries(thread.company_lookups).forEach(([id, name]) => {
        companyLookups[name as string] = id as string;
      });
    }

    // Check if companies have changed
    const newCompanyIds = new Set(Object.values(companyLookups));
    const companiesChanged = !this.areSetsEqual(this.previousCompanyIds, newCompanyIds);

    // Only update company-related state if companies have changed
    if (companiesChanged) {
      this.companyLookups = companyLookups;
      this.selectedCompanyIds = newCompanyIds;
      this.previousCompanyIds = newCompanyIds;

      // Convert companyLookups to SavedListCompanyItem array
      this.selectedCompanies = Object.entries(this.companyLookups).map(([name, id]) => ({
        id: id.replace('-group', ''),
        name: name,
        group: id.includes('-group'),
      }));

      // Reset in-depth selected company to first company
      this.inDepthSelectedCompanyId =
        this.selectedCompanies.length > 0 ? this.selectedCompanies[0].id : null;

      // Determine which tabs to show based on company count
      this.configureTabsFromCompanyCount(this.selectedCompanies.length);

      // Generate QuickBuilds for all relevant modes (only when companies change)
      this.updateAllQuickBuilds();

      // Create active lists for company-list-editor
      this.updateActiveLists();
    }

    // Track artifact pending state. Snapshot threads never produce artifacts,
    // so don't surface a pending Artifact tab waiting on one that won't arrive.
    // (mode is left unset on legacy threads — only suppress when explicitly snapshot.)
    const isSnapshotThread = thread.mode === Chat.ModeEnum.Snapshot;
    this.artifactPending = !isSnapshotThread && !!thread.artifact_pending;

    // Extract quickbuilds from all messages (always update these as new messages may have new quickbuilds)
    this.chatQuickBuilds = this.extractQuickBuildsFromThread(thread);
    this.artifacts = this.extractArtifacts(thread.messages);
    this.selectedQuickBuildIndex = 0;

    // Set the first quickbuild as active for the filters
    if (this.chatQuickBuilds.length > 0) {
      this.quickBuildForFilters = this.chatQuickBuilds[0];

      // Extract filters from quickbuilds
      const firstQB = this.chatQuickBuilds[0];
      this.extractFiltersFromQuickBuild(firstQB);
    }

    // Reconfigure tabs now that chatQuickBuilds is populated (ensures Filters tab appears when quickbuilds exist)
    this.configureTabsFromCompanyCount(this.selectedCompanies.length);

    // Handle ui_directives (e.g. show_download) only when thread is done
    this.directiveHandledNavigation = false;
    if (thread.status === 'idle') {
      this.handleUiDirectives(thread);
    }

    // Track whether the current insights source has data.
    // Shared threads only support artifacts — other tabs won't render.
    const currentHasInsights = this.isSharedThread
      ? this.artifacts.size > 0 || this.artifactPending
      : this.selectedCompanies.length > 0 ||
        this.chatQuickBuilds.length > 0 ||
        this.artifacts.size > 0 ||
        this.artifactPending;
    this.primaryThreadHasInsights = currentHasInsights;

    // Set sidenav width to 50%
    this.updateSidenavWidth();

    // Only open sidenav if we have data to show.
    // Shared threads: only open for artifacts. Normal threads: companies or quickbuilds.
    // Skip auto-open if a ui_directive already handled navigation (e.g. show_download)
    // Skip auto-open on mobile (over mode) — it covers the entire chat
    if (!this.directiveHandledNavigation && this.sidenavMode !== 'over') {
      const shouldAutoOpen = this.isSharedThread
        ? this.artifacts.size > 0
        : (companiesChanged && this.selectedCompanies.length > 0) || this.chatQuickBuilds.length > 0;
      if (shouldAutoOpen) {
        // Open sidenav after 300ms delay
        if (this.navigationTimeout) {
          window.clearTimeout(this.navigationTimeout);
        }
        this.navigationTimeout = window.setTimeout(() => {
          if (this.sidenav) {
            this.sidenav.open();
          }
        }, 300);
      }
    }
  }

  /**
   * Helper method to compare two sets for equality
   */
  private areSetsEqual(set1: Set<string>, set2: Set<string>): boolean {
    if (set1.size !== set2.size) {
      return false;
    }
    for (const item of set1) {
      if (!set2.has(item)) {
        return false;
      }
    }
    return true;
  }

  /**
   * Extract quickbuilds from thread messages
   */
  private extractQuickBuildsFromThread(thread: any): QuickBuild[] {
    const quickBuildsMap = new Map<string, QuickBuild>();

    if (thread.messages) {
      thread.messages.forEach((msg: any) => {
        if (msg.role === 'assistant' && msg.quickbuilds) {
          msg.quickbuilds.forEach((qb: QuickBuild) => {
            qb.name = qb.name || qb.rootFilterGroup.name || 'QuickBuild';
            const qbId = qb.rootFilterGroup.id?.toString() || '';
            if (qbId && !quickBuildsMap.has(qbId)) {
              quickBuildsMap.set(qbId, qb);
            }
          });
        }
      });
    }

    return Array.from(quickBuildsMap.values());
  }

  /**
   * Extract job functions, levels, and role from a quickbuild
   */
  private extractFiltersFromQuickBuild(quickBuild: QuickBuild) {
    // Extract job functions
    const functionFilters = this.getFiltersFromQuickBuild(quickBuild, 'jobs.function');
    const jobFunctions = new Set<JobFunctions>();
    functionFilters.forEach((filter) => {
      (filter.filter as SearchFilter).string_values?.forEach((value) => {
        jobFunctions.add(value as JobFunctions);
      });
    });
    this.selectedJobFunctions = Array.from(jobFunctions);

    // Extract job levels
    const levelFilters = this.getFiltersFromQuickBuild(quickBuild, 'jobs.level');
    const jobLevels = new Set<JobLevels>();
    levelFilters.forEach((filter) => {
      (filter.filter as SearchFilter).string_values?.forEach((value) => {
        jobLevels.add(value as JobLevels);
      });
    });
    this.selectedJobLevels = Array.from(jobLevels);
  }

  /**
   * Get filters of a specific type from a quickbuild
   */
  private getFiltersFromQuickBuild(quickBuild: QuickBuild, fieldName: string): UIFilter[] {
    const results: UIFilter[] = [];

    const traverse = (filters: (UIFilter | UIFilterGroup)[]) => {
      filters.forEach((item) => {
        if ('operator' in item) {
          // This is a filter group
          traverse(item.filters);
        } else if ('filter' in item) {
          // This is a filter
          if (item.filter.field === fieldName) {
            results.push(item);
          }
        }
      });
    };

    if (quickBuild.rootFilterGroup?.filters) {
      traverse(quickBuild.rootFilterGroup.filters);
    }

    return results;
  }

  updateActiveLists() {
    // In Depth: only first company
    this.inDepthActiveList = {
      id: null,
      name: 'Selected Companies',
      companies: this.selectedCompanies,
    };

    // Head-to-Head and Benchmark: all companies
    this.headToHeadActiveList = {
      id: null,
      name: 'Selected Companies',
      companies: this.selectedCompanies,
    };

    this.benchmarkActiveList = {
      id: null,
      name: 'Selected Companies',
      companies: this.selectedCompanies,
    };
  }

  configureTabsFromCompanyCount(companyCount: number) {
    // Configure available tabs based on company count from company_lookups
    const tabs: TabConfig[] = [];

    if (companyCount >= 1) {
      // At least 1 company -> In Depth available
      tabs.push({ index: 0, label: 'In Depth', icon: 'bar_chart', enabled: true });
      this.activeTabIndex = 0; // Default to In Depth
    }

    if (companyCount > 1) {
      // 2+ companies -> Head-to-Head and Benchmark available
      tabs.push({ index: 1, label: 'Head-to-Head', icon: 'compare_arrows', enabled: true });
      tabs.push({ index: 2, label: 'Benchmark', icon: 'leaderboard', enabled: true });
    }

    // Data tab available when there are quickbuilds
    if (this.chatQuickBuilds.length > 0) {
      tabs.push({ index: 3, label: 'Filters', icon: 'table_chart', enabled: true });

      // If no companies but we have quickbuilds, default to Data tab
      if (companyCount === 0 && this.chatQuickBuilds.length > 0) {
        this.activeTabIndex = 3;
      }
    }

    // Add Artifact tab when artifacts exist or are pending
    if (this.artifacts.size > 0 || this.artifactPending) {
      tabs.push({ index: 4, label: 'Artifact', icon: 'code', enabled: true });
      this.activeTabIndex = 4;
    }

    this.availableTabs = tabs;
  }

  isTabAvailable(index: number): boolean {
    return this.availableTabs.some((tab) => tab.index === index);
  }

  hasCompanyTabs(): boolean {
    // Check if any company-related tabs (In Depth, Head-to-Head, Benchmark) are available
    return this.availableTabs.some((tab) => tab.index >= 0 && tab.index <= 2);
  }

  updateAllQuickBuilds() {
    this.peopleReportService.startCacheContext();

    // Generate QuickBuilds for all three modes
    this.inDepthQuickBuild = this.generateInDepthQB();
    this.headToHeadQBs = this.generateHeadToHeadQBs();
    this.benchmarkQBs = this.generateBenchmarkQBs();
  }

  onQuickBuildSelectionChange(index: number) {
    this.selectedQuickBuildIndex = index;
    // Update the quickbuild for the filters component
    if (this.chatQuickBuilds.length > 0 && index < this.chatQuickBuilds.length) {
      this.quickBuildForFilters = this.chatQuickBuilds[index];
    }
  }

  downloadData() {
    if (!this.quickBuildForFilters || !this.matchingRecords) {
      return;
    }

    this.drawerService.openDrawer({
      quickBuild: this.quickBuildForFilters,
      mode: 'data-selection',
      matchingRecords: this.matchingRecords,
      title: this.quickBuildForFilters.name || 'Data Export',
    });
  }

  generateInDepthQB(): QuickBuild | null {
    if (this.selectedCompanies.length === 0) {
      return null;
    }
    return this.generateQuickBuildForCompanies([this.selectedCompanies[0]]);
  }

  generateHeadToHeadQBs(): QuickBuild[] {
    if (this.selectedCompanies.length === 0) {
      return [];
    }
    return this.selectedCompanies.map((c) => this.generateQuickBuildForCompanies([c]));
  }

  generateBenchmarkQBs(): QuickBuild[] {
    if (this.selectedCompanies.length === 0) {
      return [];
    }
    return this.selectedCompanies.map((c) => this.generateQuickBuildForCompanies([c]));
  }

  generateQuickBuildForCompanies(companies: SavedListCompanyItem[]): QuickBuild {
    const filters: (UIFilter | UIFilterGroup)[] = [];

    // Company filter
    filters.push({
      filter: {
        field: 'jobs_search',
        match_type: SearchFilter.MatchTypeEnum.Exact,
        string_values: companies.map((c) => c.id! + (c.group ? '-group' : '')),
        type: SearchFilter.TypeEnum.Must,
      },
    });

    // Functions filter
    if (
      this.selectedJobFunctions &&
      this.selectedJobFunctions.length > 0 &&
      this.selectedJobFunctions.length !== Object.values(JobFunctions).length
    ) {
      filters.push({
        filter: {
          field: 'jobs.function',
          match_type: SearchFilter.MatchTypeEnum.Exact,
          string_values: this.selectedJobFunctions,
          type: SearchFilter.TypeEnum.Must,
        },
      });
    }

    // Levels filter
    if (
      this.selectedJobLevels &&
      this.selectedJobLevels.length > 0 &&
      this.selectedJobLevels.length !== Object.values(JobLevels).length
    ) {
      filters.push({
        filter: {
          field: 'jobs.level',
          match_type: SearchFilter.MatchTypeEnum.Exact,
          string_values: this.selectedJobLevels,
          type: SearchFilter.TypeEnum.Must,
        },
      });
    }

    if (this.selectedRole) {
      filters.push({
        filter: {
          field: 'job_role',
          name: this.selectedRole.name,
          match_type: SearchFilter.MatchTypeEnum.Exact,
          string_values: [this.selectedRole.name],
          type: SearchFilter.TypeEnum.Must,
        },
      });
    }

    return {
      id: UniqueIdGenerator.generate(),
      name: companies.map((c) => c.name!).join(', '),
      rootFilterGroup: {
        id: UniqueIdGenerator.generate(),
        operator: 'and',
        filters: [
          {
            id: 'moneyball',
            operator: 'and',
            isJobsGroup: true,
            filters: filters,
          },
        ],
      },
    };
  }

  onTabChange(index: number) {
    this.activeTabIndex = index;
  }

  updateSidenavWidth() {
    if (this.sidenavMode === 'over') {
      // In overlay mode, use full screen width on small screens, 80% on larger
      const screenWidth = window.innerWidth;
      const targetWidth = screenWidth <= 640
        ? screenWidth  // Full width on mobile
        : Math.floor(screenWidth * 0.8);

      // Constrain to min and screen width
      this.sidenavWidth = Math.min(Math.max(this.sidenavMinWidth, targetWidth), screenWidth);
    } else {
      // In side mode, calculate 50% of screen width
      const halfWidth = Math.floor(window.innerWidth / 2);

      // Ensure chat area stays at least chatMinWidth (500px)
      const maxAllowedWidth = window.innerWidth - this.chatMinWidth;

      // Set width to 50% but constrain to min and chat minimum
      this.sidenavWidth = Math.max(this.sidenavMinWidth, Math.min(halfWidth, maxAllowedWidth));
    }
  }

  // Resize handlers
  startResize(event: MouseEvent): void {
    // Don't allow resizing in overlay mode
    if (this.sidenavMode === 'over') {
      return;
    }

    this.isResizing = true;
    this.startX = event.clientX;
    this.startWidth = this.sidenavWidth;
    event.preventDefault();
  }

  @HostListener('document:mousemove', ['$event'])
  onResize(event: MouseEvent): void {
    if (!this.isResizing) {
      return;
    }

    const deltaX = this.startX - event.clientX;
    let newWidth = this.startWidth + deltaX;

    if (this.sidenavMode === 'over') {
      // In overlay mode, allow up to 80% of screen width
      const maxAllowedWidth = Math.floor(window.innerWidth * 0.8);
      newWidth = Math.max(this.sidenavMinWidth, Math.min(newWidth, maxAllowedWidth));
    } else {
      // In side mode, ensure chat area stays at least chatMinWidth
      const maxAllowedWidth = window.innerWidth - this.chatMinWidth;
      newWidth = Math.max(this.sidenavMinWidth, Math.min(newWidth, maxAllowedWidth));
    }

    this.sidenavWidth = newWidth;

    // Trigger resize event to force child components to re-evaluate responsive styles
    window.dispatchEvent(new Event('resize'));
  }

  @HostListener('document:mouseup')
  stopResize(): void {
    this.isResizing = false;

    // Final resize event after user finishes resizing to ensure all components recalculate
    // Clear any existing timeout first
    if (this.resizeTimeout) {
      window.clearTimeout(this.resizeTimeout);
    }
    this.resizeTimeout = window.setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 50);
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    // Don't interfere with manual resizing
    if (this.isResizing || !this.sidenav?.opened) {
      return;
    }

    // Recalculate sidenav width to maintain constraints when window resizes
    if (this.sidenavMode === 'over') {
      // In overlay mode, constrain to 80% max
      const maxAllowedWidth = Math.floor(window.innerWidth * 0.8);
      this.sidenavWidth = Math.max(
        this.sidenavMinWidth,
        Math.min(this.sidenavWidth, maxAllowedWidth)
      );
    } else {
      // In side mode, ensure chat area maintains minimum width
      const maxAllowedWidth = window.innerWidth - this.chatMinWidth;
      this.sidenavWidth = Math.max(
        this.sidenavMinWidth,
        Math.min(this.sidenavWidth, maxAllowedWidth)
      );
    }
  }

  onChartConfigChange(tabName: string, chartId: string, config: any) {
    (this.chartConfigs as any)[chartId] = config;
  }

  onInDepthCompanySelectionChange(selectedCompanies: Set<SavedListCompanyItem>) {
    // Update In Depth QuickBuild based on selected company
    const companiesArray = Array.from(selectedCompanies);
    if (companiesArray.length > 0) {
      // Update the selected company ID to track which one is highlighted
      this.inDepthSelectedCompanyId = companiesArray[0].id;
      this.inDepthQuickBuild = this.generateQuickBuildForCompanies([companiesArray[0]]);
    }
  }

  getCompanyNameById(companyId: string): string {
    // Find company name from companyLookups
    const entry = Object.entries(this.companyLookups).find(
      ([name, id]) => id.replace('-group', '') === companyId
    );
    return entry ? entry[0] : companyId;
  }

  /**
   * Check if there are any insights to show (companies or quickbuilds).
   * Shared threads only support artifacts — companies/quickbuilds won't render.
   */
  get hasInsights(): boolean {
    if (this.isSharedThread) {
      return this.artifacts.size > 0 || this.artifactPending;
    }
    return (
      this.primaryThreadHasInsights ||
      this.selectedCompanies.length > 0 ||
      this.chatQuickBuilds.length > 0 ||
      this.artifacts.size > 0 ||
      this.artifactPending
    );
  }

  /**
   * Extract artifacts from chat messages
   */
  private extractArtifacts(messages: any[]): Map<number, Artifact> {
    const artifacts = new Map<number, Artifact>();

    messages.forEach((message, index) => {
      // Check if this assistant message has an artifact
      if (message.role === 'assistant') {
        if (message.artifact) {
          const id = message.artifact.id || `artifact-${index}`;
          const type = message.artifact.type || 'highcharts';
          const code = message.artifact.code;
          const height = message.artifact.height || '600px';

          // Reuse existing artifact object if content hasn't changed to avoid unnecessary re-renders
          const existing = this.artifacts.get(index);
          if (
            existing &&
            existing.id === id &&
            existing.type === type &&
            existing.code === code &&
            existing.height === height
          ) {
            artifacts.set(index, existing);
          } else {
            artifacts.set(index, { id, type, code, height });
          }
        }
      }
    });

    // Only update selectedArtifact if artifacts actually changed
    const newArray = Array.from(artifacts.values());
    const oldArray = Array.from(this.artifacts.values());
    const artifactsChanged =
      newArray.length !== oldArray.length ||
      newArray.some((a, i) => a !== oldArray[i]);

    if (artifactsChanged) {
      this.selectedArtifactIndex = newArray.length > 0 ? newArray.length - 1 : 0;
      this.selectedArtifact = newArray.length > 0 ? newArray[newArray.length - 1] : undefined;
    }

    return artifacts;
  }

  /**
   * Extract ui_directives from thread messages and handle them
   */
  private handleUiDirectives(thread: any) {
    if (!thread.messages) return;

    thread.messages.forEach((msg: any, msgIndex: number) => {
      if (msg.role !== 'assistant' || !msg.ui_directives) return;

      msg.ui_directives.forEach((directive: UiDirective) => {
        if (directive.action === 'show_support_options') {
          const key = `${thread.id}_${msgIndex}_${directive.action}_${directive.iteration || 0}`;
          if (this.processedDirectiveKeys.has(key)) return;
          this.processedDirectiveKeys.add(key);

          this.directiveTimeouts.push(window.setTimeout(() => {
            if ((window as any).Intercom) {
              (window as any).Intercom('update', { skip_fin: true });
              (window as any).Intercom('showNewMessage');
            }
          }, 500));
          return;
        }

        if (directive.action !== 'show_download') return;

        // Build a unique key to avoid re-processing the same directive
        const key = `${thread.id}_${msgIndex}_${directive.action}_${directive.iteration || 0}`;
        if (this.processedDirectiveKeys.has(key)) return;
        this.processedDirectiveKeys.add(key);

        // Resolve quickbuild: look up by quickbuild_id from chat quickbuilds, or use inline quickbuild
        let qb: QuickBuild | undefined;
        if (directive.metadata?.quickbuild_id) {
          qb = this.chatQuickBuilds.find(
            (existing) => existing.rootFilterGroup?.id?.toString() === directive.metadata!.quickbuild_id
          );
          if (!qb) {
            Log.e('show_download directive references unknown quickbuild_id:', directive.metadata.quickbuild_id);
            return;
          }
        } else if (directive.metadata?.quickbuild) {
          // Legacy: inline quickbuild object
          qb = {
            id: directive.metadata.quickbuild.rootFilterGroup?.id || UniqueIdGenerator.generate(),
            name: directive.metadata.quickbuild.rootFilterGroup?.name || directive.metadata.suggested_filename || 'Download',
            rootFilterGroup: directive.metadata.quickbuild.rootFilterGroup,
          };
        } else {
          return;
        }

        // Inject this quickbuild into the chat quickbuilds if not already present
        const existingIndex = this.chatQuickBuilds.findIndex(
          (existing) => existing.rootFilterGroup?.id === qb.rootFilterGroup?.id
        );
        let targetIndex = 0;
        if (existingIndex === -1) {
          this.chatQuickBuilds.unshift(qb);
        } else {
          targetIndex = existingIndex;
        }

        // Set this quickbuild as active in the Explore tab
        this.quickBuildForFilters = qb;
        this.selectedQuickBuildIndex = targetIndex;

        // Ensure the Explore/Filters tab is available and switch to it
        this.configureTabsFromCompanyCount(this.selectedCompanies.length);
        this.activeTabIndex = 3;

        // Flag that directive handled navigation (prevents auto-open from overwriting)
        this.directiveHandledNavigation = true;

        // Open the sidenav
        this.updateSidenavWidth();
        if (this.navigationTimeout) {
          window.clearTimeout(this.navigationTimeout);
        }
        this.navigationTimeout = window.setTimeout(() => {
          if (this.sidenav) {
            this.sidenav.open();
          }
        }, 300);

        // Open the download drawer (fixed overlay, independent of sidenav)
        this.directiveTimeouts.push(window.setTimeout(() => {
          const drawerConfig: DrawerConfig = {
            quickBuild: qb,
            mode: 'data-selection',
            matchingRecords: null,
            title: directive.metadata!.suggested_filename?.replace('.csv', '') || qb.name || 'Data Export',
            customColumns: directive.metadata!.columns || null,
            suggestedFilename: directive.metadata!.suggested_filename || null,
          };
          this.drawerService.openDrawer(drawerConfig);
        }, 500));
      });
    });
  }

  /**
   * Get artifact for a specific message
   */
  getArtifactForMessage(messageIndex: number): Artifact | undefined {
    return this.artifacts.get(messageIndex);
  }

  /**
   * Select an artifact to display
   */
  selectArtifact(messageIndex: number) {
    this.selectedArtifact = this.artifacts.get(messageIndex);

    // Track analytics
    // HeapUtils.track('chat_artifact_viewed', {
    //   threadId: this.threadId,
    //   artifactType: this.selectedArtifact?.type,
    // });
  }

  /**
   * Check if any artifacts exist in the conversation
   */
  hasAnyArtifacts(): boolean {
    return this.artifacts.size > 0;
  }

  /**
   * Get artifacts as an array for iteration
   */
  get artifactsArray(): Artifact[] {
    return Array.from(this.artifacts.values());
  }

  /**
   * Handle artifact selection change
   */
  onArtifactSelectionChange(index: number) {
    this.selectedArtifactIndex = index;
    const artifactsArray = this.artifactsArray;
    if (index >= 0 && index < artifactsArray.length) {
      this.selectedArtifact = artifactsArray[index];
    }
  }

  /**
   * Paste artifact code from clipboard for testing (admin only)
   * Copy the "code" field value from API response, then click this button
   */
  async pasteArtifactData(): Promise<void> {
    try {
      // Read from clipboard
      const codeInput = await navigator.clipboard.readText();

      if (!codeInput || !codeInput.trim()) {
        this.notifyService.warning('Clipboard is empty');
        return;
      }

      // Process escape sequences if pasting from JSON
      let processedCode = codeInput.trim();

      // If it looks like JSON-escaped content (contains literal \n), process it
      if (processedCode.includes('\\n')) {
        processedCode = processedCode
          .replace(/\\n/g, '\n')
          .replace(/\\t/g, '\t')
          .replace(/\\"/g, '"')
          .replace(/\\'/g, "'");
      }

      // Create artifact
      const artifact: Artifact = {
        id: `pasted-artifact-${Date.now()}`,
        type: 'highcharts',
        code: processedCode,
        height: '1400px',
      };

      // Add to artifacts map with a special index (-1 for pasted artifacts)
      const pastedIndex = -1;
      this.artifacts.set(pastedIndex, artifact);

      this.selectArtifact(pastedIndex);

      this.notifyService.success('Artifact pasted from clipboard');
    } catch (error) {
      if (error instanceof DOMException && error.name === 'NotAllowedError') {
        this.notifyService.error('Clipboard access denied. Please grant permission and try again.');
      } else {
        this.notifyService.error('Error reading from clipboard. Please try again.');
        Log.e('Error pasting artifact from clipboard:', error);
      }
    }
  }

  /**
   * Copy currently displayed artifact code to clipboard (admin only)
   */
  async copyArtifactCode(): Promise<void> {
    try {
      if (!this.selectedArtifact) {
        this.notifyService.warning('No artifact is currently displayed');
        return;
      }

      // Copy the artifact code to clipboard
      await navigator.clipboard.writeText(this.selectedArtifact.code);
      this.notifyService.success('Artifact code copied to clipboard');
    } catch (error) {
      if (error instanceof DOMException && error.name === 'NotAllowedError') {
        this.notifyService.error('Clipboard access denied. Please grant permission and try again.');
      } else {
        this.notifyService.error('Error copying to clipboard. Please try again.');
        Log.e('Error copying artifact code to clipboard:', error);
      }
    }
  }

  /**
   * Navigate to the standalone insights page with the current chat state.
   * Opens in a new tab using the ?state= query parameter.
   */
  navigateToInsights(mode?: 'company' | 'battle' | 'benchmark') {
    if (!mode) {
      // Map the active tab index to an insights mode
      if (this.activeTabIndex === 0) {
        mode = 'company';
      } else if (this.activeTabIndex === 2) {
        mode = 'benchmark';
      } else {
        mode = 'battle';
      }
    }

    // Build a PageState-compatible object for the insights page
    const activeList = {
      id: null as string | null,
      name: 'From Chat',
      companies: this.selectedCompanies,
    };

    // For company mode, only include the selected company
    let selectedCompanyIds = this.selectedCompanies.map((c) => c.id);
    if (mode === 'company' && this.inDepthSelectedCompanyId) {
      selectedCompanyIds = [this.inDepthSelectedCompanyId];
    }

    const state = {
      activeList,
      dateRangeStrings: this.dateRangeStrings,
      selectedJobFunctions: this.selectedJobFunctions || [],
      selectedJobLevels: this.selectedJobLevels || [],
      selectedRole: this.selectedRole,
      mode,
      selectedCompanyIds,
      chartConfigs: this.chartConfigs,
    };

    const encodedState = btoa(JSON.stringify(state));
    const url = `/insights?state=${encodedState}`;
    window.open(url, '_blank');
  }

  // Chat navigation methods
  toggleHistoryDrawer() {
    this.historyDrawerOpen = !this.historyDrawerOpen;
  }

  startNewChat() {
    // Clear active thread and navigate to chat page without threadId
    this.activeThreadId = undefined;
    this.primaryThreadHasInsights = false;
    this.isThreadBusy = false;
    this.router.navigate(['/workforce', 'chat']);
  }

  private addClickInterception() {
    this.removeClickInterception();

    this.palModeClickHandler = (event: Event) => {
      const target = event.target as Element;
      const isInsideLoginDialog =
        target.closest('.login-dialog-container') ||
        target.closest('.mat-mdc-dialog-container') ||
        target.closest('.mat-dialog-container') ||
        target.closest('.login-dialog-backdrop') ||
        target.closest('#login-signup') ||
        target.closest('.login-signup-main');

      if (!isInsideLoginDialog) {
        this.loginDialogService.openLoginDialog();
        HeapUtils.track('public_share_chat_signup_dialog_shown', {});
        event.stopPropagation();
        event.preventDefault();
      }
    };

    document.addEventListener('click', this.palModeClickHandler, true);
  }

  private removeClickInterception() {
    if (this.palModeClickHandler) {
      document.removeEventListener('click', this.palModeClickHandler, true);
      this.palModeClickHandler = null;
    }
  }

  shareChat(): void {
    if (!this.activeThreadId) return;
    const thread = this.peopleChatService.getThreadSnapshot(this.activeThreadId);
    if (!thread) return;
    const config = {
      messages: thread.messages.filter((m: any) => m.role !== 'thinking'),
      company_lookups: thread.company_lookups,
      created_at: thread.created_at,
    };
    this.shareThisViewService.showShareDialog(this.orgId, '/chat', config);
  }

  onMessageSubmitted(submission: NewChatSubmission) {
    this.isThreadBusy = true;
    // Create a new chat with the first message using the service
    this.peopleChatService
      .createThread(this.orgId, submission.prompt, submission.mode)
      .pipe(
        takeUntil(this.destroy$),
        takeUntil(this.cancelCurrentThreadLoad$) // Cancel if user switches threads
      )
      .subscribe({
        next: (thread) => {
          // Navigate to the new chat with threadId
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { threadId: thread.id },
            queryParamsHandling: 'merge',
          });
        },
        error: (error) => {
          console.error('Error creating chat:', error);
          // TODO: Show error notification
        },
      });
  }
}
